import { api } from './api';
import { connections } from './connections';

/**
 * Web Push. Auf Android/Chrome sofort verfügbar, auf iOS erst ab 16.4 und
 * nur, wenn die App vorher zum Home-Bildschirm hinzugefügt wurde.
 */

export type PushState =
  | 'unsupported'      // Browser kann kein Push
  | 'needs-install'    // iOS: erst zum Home-Bildschirm hinzufügen
  | 'server-disabled'  // Server hat keine VAPID-Schlüssel
  | 'denied'           // Nutzer hat abgelehnt
  | 'prompt'           // noch nicht gefragt
  | 'granted';         // läuft

export const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS gibt sich als Mac aus, hat aber einen Touchscreen.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

export function pushSupport(): PushState {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // Genau hier landet iOS im normalen Safari-Tab.
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  return 'prompt';
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch (error) {
    console.warn('[push] Service Worker konnte nicht registriert werden:', error);
    return null;
  }
}

/** VAPID-Schlüssel von base64url in das Uint8Array, das die Push-API erwartet. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export interface EnablePushResult {
  state: PushState;
  message?: string;
}

interface SubscriptionResult extends EnablePushResult {
  subscription?: PushSubscriptionJSON;
}

const NEEDS_INSTALL_MESSAGE =
  'Auf dem iPhone gehen Benachrichtigungen nur aus der installierten App: ' +
  'in Safari auf "Teilen" tippen, dann "Zum Home-Bildschirm", und die App von dort öffnen.';
const UNSUPPORTED_MESSAGE = 'Dieser Browser unterstützt keine Benachrichtigungen.';
const SERVER_DISABLED_MESSAGE = 'Auf dem Server fehlen die VAPID-Schlüssel (siehe README: npm run vapid).';
const DENIED_MESSAGE =
  'Ohne Erlaubnis können wir nicht Bescheid geben. Du kannst das in den ' +
  'Browser-Einstellungen für diese Seite wieder ändern.';

/**
 * Fragt die Erlaubnis ab und liefert eine Push-Subscription - unabhängig
 * davon, ob sie danach für eine einzelne Fahrt oder gerätegebunden für einen
 * Kontakt registriert wird (dieselbe Browser-Subscription dient für beides).
 */
async function ensurePushSubscription(): Promise<SubscriptionResult> {
  const support = pushSupport();
  if (support === 'needs-install') return { state: support, message: NEEDS_INSTALL_MESSAGE };
  if (support === 'unsupported') return { state: support, message: UNSUPPORTED_MESSAGE };

  const config = await api.config();
  if (!config.pushEnabled || !config.vapidPublicKey) {
    return { state: 'server-disabled', message: SERVER_DISABLED_MESSAGE };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { state: 'denied', message: DENIED_MESSAGE };

  const registration = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.vapidPublicKey) as BufferSource,
    }));

  return { state: 'granted', subscription: subscription.toJSON() };
}

/** Aktiviert Push für eine einzelne Fahrt (klassischer Code-Beitritt). */
export async function enablePush(rideId: string, token: string): Promise<EnablePushResult> {
  const result = await ensurePushSubscription();
  if (result.state !== 'granted' || !result.subscription) return result;
  await api.subscribe(rideId, token, result.subscription);
  return { state: 'granted' };
}

/**
 * Aktiviert Push gerätegebunden, unabhängig von einer Fahrt - für Kontakte.
 * Wird im Bestätigen-Bildschirm einer Einladung aufgerufen, dem einzigen
 * Moment ohne aktive Fahrt, in dem der Browser die nötige Nutzer-Geste hat.
 */
export async function enableDevicePush(): Promise<EnablePushResult> {
  const result = await ensurePushSubscription();
  if (result.state !== 'granted' || !result.subscription) return result;
  await connections.subscribePush(result.subscription);
  return { state: 'granted' };
}
